// src/screens/recruit/RecruitWriteScreen.js
// 21차: 장르·지역·상세지역 드럼롤 스크롤 피커로 변경, 한 줄 레이아웃
//       날짜·시간 한 줄 배치, KeyboardAvoidingView 개선
// 25차: edit 모드 추가 (route.params.recruit 받으면 수정 모드)
//       재모집 시 작성 화면으로 이동 지원 (isReRecruit 플래그)
//       오픈채팅 링크 → 모집 설명 아래로 이동
//       설명 placeholder 예시 추가
//       시간 선택 시 이미 예약된 시간 흐리게 표시
//       뱃지 빨간색 통일
// 51차: 색 theme.js 단일화 — 자체 색 정의 제거, COLORS 참조 / 초록·빨강·파랑·주황 통일
// 71차: ThemePickerModal — 존재하지 않는 store.name 참조하던 죽은 fallback 제거,
//       productService.getStoreDisplayName()으로 교체 (지점명 폴백도 함께 적용됨)
// 74차: "크루원 모집으로 등록" 토글 추가(내 크루 있을 때만, 신규 작성 시에만) — 켜면
//       recruits 문서에 crewId를 남기고 크루 게시판(crewPosts)에도 카드가 자동으로 뜨게 연동
// 76차: 커스텀 헤더(뒤로가기+제목+등록/저장 버튼) → 공통 AppHeader로 교체
//       (rightElement에 기존 등록/저장 버튼 그대로 이동, 동작 변화 없음)
// 78차: 헤더 rightElement의 "등록"/"저장" 버튼 삭제 — GradientButton은 style prop이
// 외곽 TouchableOpacity에만 적용되고 실제 골드 박스(LinearGradient)는 자체 paddingVertical:14를
// 갖고 있어, 헤더용으로 style={{paddingVertical:7}}을 덧씌워도 두 패딩이 합산돼 버튼이
// 비정상적으로 커 보였음(사용자 신고: "등록 버튼 크기가 이상해"). 하단에 동일 동작을 하는
// 정상 크기의 "모집 글 등록"/"수정 완료" GradientButton이 이미 있어 중복이기도 해서,
// 패치 대신 헤더 버튼 자체를 제거.

// 78차 32차: 아이콘/기호(✕, ‹, ›) 전용 닫기·월이동 버튼에 accessibilityLabel 추가(접근성 라벨 전수 점검)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView,
  StyleSheet, ActivityIndicator, Modal, FlatList,
  KeyboardAvoidingView, Platform, Keyboard,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { createRecruit, updateRecruit, checkRecruitBan } from '../../services/recruitService';
import { getBookedTimes } from '../../services/reservationService';
import { addCrewRecruitLink } from '../../services/crewService';
import { getStoreDisplayName, getProductById } from '../../services/productService';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { SecondaryButton } from '../../components/common/Buttons';
import { MenuReservationIcon, MenuPointsIcon, IconLabel, LocationIcon, GenreIcon, CancelIcon, NoShowPenaltyIcon, RefreshIcon, CheckmarkIcon } from '../../components/common/Icons';
import { LogoMark } from '../../components/common/AppHeader';
import { COLORS } from '../../constants/theme';
import { isAdultBirth, isConfirmedMinorBirth } from '../../utils/dateUtils';

let GENRES_RAW = [], REGIONS_RAW = [], SUB_REGIONS_RAW = {};
try {
  const c = require('../../constants/index');
  GENRES_RAW      = Array.isArray(c.GENRES)                            ? c.GENRES      : [];
  REGIONS_RAW     = Array.isArray(c.REGIONS)                           ? c.REGIONS     : [];
  SUB_REGIONS_RAW = c.SUB_REGIONS && typeof c.SUB_REGIONS === 'object' ? c.SUB_REGIONS : {};
} catch {}

const GENRES = GENRES_RAW.length > 0 ? GENRES_RAW
  : ['공포', '추리', '미스터리', '판타지', '스릴러', '액션', '로맨스', '코미디', 'SF', '기타'];
const REGIONS = REGIONS_RAW.length > 0 ? REGIONS_RAW
  : ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '기타'];
const SUB_REGIONS = Object.keys(SUB_REGIONS_RAW).length > 0 ? SUB_REGIONS_RAW : {
  서울: ['강남', '홍대', '신촌', '건대', '혜화', '이태원', '종로', '기타'],
  경기: ['수원', '성남', '고양', '용인', '기타'],
  인천: ['부평', '송도', '기타'],
  부산: ['서면', '해운대', '기타'],
  대구: ['동성로', '기타'],
  광주: ['충장로', '기타'],
  대전: ['둔산', '기타'],
  기타: ['기타'],
};

const GOLD = COLORS.gold, GOLD_BG = COLORS.goldBg, GOLD_BDR = COLORS.goldBorder;
const BG = COLORS.bg, SURFACE = COLORS.surface, SURFACE2 = COLORS.surface2;
const BORDER = COLORS.border, TEXT = COLORS.text;
const TEXT_SEC = COLORS.textSec, TEXT_MUTED = COLORS.textMuted;
const GREEN = COLORS.green, RED = COLORS.red, BLUE = COLORS.blue, ORANGE = COLORS.orange;
const ON_GOLD = COLORS.onGold;
const ITEM_H = 44;

const DESC_PLACEHOLDER = `예) 공포 테마 고수분들 환영합니다 🙌

- 탈출 경험: 10회 이상 선호
- 분위기: 진지하게 집중해서 즐기고 싶어요
- 당일 카카오 오픈채팅으로 연락드립니다

초보분도 괜찮으시면 편하게 신청해주세요!`;

// 80차 후속: 체험형은 방탈출처럼 "정원을 채워야 갈 수 있다"는 코디네이션 문제가 아니라
// "같이 보고 싶어서" 동행을 구하는 것에 가까워, 예시 문구만 그 톤에 맞게 다르게 보여줌.
const SHOW_DESC_PLACEHOLDER = `예) 같이 볼 사람 구해요! 🎭

- 관람 스타일: 편하게 즐기고 싶어요
- 당일 카카오 오픈채팅으로 연락드립니다

처음이신 분도 편하게 신청해주세요!`;

// ── 드럼롤 스크롤 피커 ────────────────────────────────────────
function DrumPicker({ items, selected, onSelect, placeholder = '선택' }) {
  const scrollRef = useRef(null);
  const selectedIdx = items.indexOf(selected);

  useEffect(() => {
    if (selectedIdx >= 0 && scrollRef.current) {
      scrollRef.current.scrollTo({ y: selectedIdx * ITEM_H, animated: false });
    }
  }, []);

  const handleScroll = (e) => {
    const y = e.nativeEvent.contentOffset.y;
    const idx = Math.round(y / ITEM_H);
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    if (items[clamped] !== selected) onSelect(items[clamped]);
  };

  return (
    <View style={dp2.drum}>
      <View pointerEvents="none" style={dp2.highlight} />
      <ScrollView
        ref={scrollRef}
        showsVerticalScrollIndicator={false}
        snapToInterval={ITEM_H}
        decelerationRate="fast"
        onMomentumScrollEnd={handleScroll}
        onScrollEndDrag={handleScroll}
        contentContainerStyle={{ paddingVertical: ITEM_H }}
        style={{ height: ITEM_H * 3 }}>
        {items.map((item, i) => {
          const isOn = item === selected;
          return (
            <TouchableOpacity
              key={i}
              style={[dp2.drumItem, { height: ITEM_H }]}
              onPress={() => onSelect(item)}
              activeOpacity={0.7}>
              {/* 78차 35차: 긴 지역/장르명이 numberOfLines 없이 2줄로 줄바꿈되면서 고정
                  높이(ITEM_H)+overflow:hidden에 위아래가 잘려 보이던 버그 — 1줄+말줄임으로 수정 */}
              <Text style={isOn ? dp2.drumItemTxtOn : dp2.drumItemTxt} numberOfLines={1} ellipsizeMode="tail">{item}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

// ── 장르·지역·상세지역 통합 피커 모달 ──────────────────────────
function LocationGenreModal({ visible, genre, region, subRegion, onConfirm, onCancel }) {
  const [selGenre,  setSelGenre]  = useState(genre    || '');
  const [selRegion, setSelRegion] = useState(region   || REGIONS[0]);
  const [selSub,    setSelSub]    = useState(subRegion || '');
  // 78차 66차: 실제 매장 데이터의 상세지역이 고정 목록에 없는 값(예: "광진")일 수 있음 —
  // 목록에 없다고 빼버리면 선택값이 통째로 사라져 보여서, 현재 값은 항상 맨 앞에 포함.
  const baseSubList = SUB_REGIONS[selRegion] || [];
  const subList = (selSub && !baseSubList.includes(selSub)) ? [selSub, ...baseSubList] : baseSubList;

  useEffect(() => {
    if (visible) {
      setSelGenre(genre || '');
      setSelRegion(region || REGIONS[0]);
      setSelSub(subRegion || '');
    }
  }, [visible]);

  const handleRegionChange = (r) => {
    setSelRegion(r);
    setSelSub('');
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <View style={dp2.overlay}>
        <View style={dp2.box}>
          <View style={dp2.modalHeader}>
            <Text style={dp2.modalTitle}>장르 · 지역 선택</Text>
            <TouchableOpacity onPress={onCancel} accessibilityLabel="닫기">
              <CancelIcon size={dp2.modalClose.fontSize || 18} color={dp2.modalClose.color} />
            </TouchableOpacity>
          </View>

          <View style={dp2.pickerRow}>
            <View style={dp2.pickerCol}>
              <Text style={dp2.pickerLabel}>장르</Text>
              <DrumPicker
                items={['(없음)', ...GENRES]}
                selected={selGenre || '(없음)'}
                onSelect={v => setSelGenre(v === '(없음)' ? '' : v)}
              />
            </View>
            <View style={dp2.pickerCol}>
              <Text style={dp2.pickerLabel}>지역 *</Text>
              <DrumPicker
                items={REGIONS}
                selected={selRegion}
                onSelect={handleRegionChange}
              />
            </View>
            <View style={dp2.pickerCol}>
              <Text style={dp2.pickerLabel}>상세지역</Text>
              {subList.length > 0 ? (
                <DrumPicker
                  key={selRegion}
                  items={['(없음)', ...subList]}
                  selected={selSub || '(없음)'}
                  onSelect={v => setSelSub(v === '(없음)' ? '' : v)}
                />
              ) : (
                <View style={[dp2.drum, { justifyContent: 'center', alignItems: 'center' }]}>
                  <Text style={{ color: TEXT_MUTED, fontSize: 12 }}>없음</Text>
                </View>
              )}
            </View>
          </View>

          <View style={dp2.btnRow}>
            <SecondaryButton label="취소" onPress={onCancel} style={dp2.cancelBtn} textStyle={dp2.cancelTxt} />
            <GradientButton
              label="확인"
              onPress={() => selRegion && onConfirm(selGenre, selRegion, selSub)}
              disabled={!selRegion}
              style={dp2.confirmBtn}
              textStyle={dp2.confirmTxt}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ── 시간 선택 Modal ───────────────────────────────────────────
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 10; h <= 22; h++) slots.push(`${String(h).padStart(2,'0')}:00`);
  return slots;
})();

// ── 단일 시각 선택 Modal (지난 시간 + 예약된 시간 흐리게) ──────
function TimePickerModal({ visible, value, selectedDate, onConfirm, onCancel, bookedTimes = [] }) {
  const [selected, setSelected] = useState(value || '');

  useEffect(() => {
    if (visible) setSelected(value || '');
  }, [visible, value]);

  const isPastSlot = (t) => {
    const now = new Date();
    const base = selectedDate ? new Date(selectedDate) : new Date();
    const today = new Date(); today.setHours(0,0,0,0);
    const baseDay = new Date(base); baseDay.setHours(0,0,0,0);
    if (baseDay.getTime() > today.getTime()) return false;
    if (baseDay.getTime() < today.getTime()) return true;
    const [h, m] = t.split(':').map(Number);
    const slotTime = new Date(); slotTime.setHours(h, m, 0, 0);
    return slotTime <= now;
  };

  const isBooked = (t) => bookedTimes.includes(t);

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <View style={dp.overlay}>
        <View style={dp.box}>
          <View style={dp.header}>
            <IconLabel Icon={MenuPointsIcon} size={15} color={TEXT} textStyle={dp.title}>시간 선택</IconLabel>
            <TouchableOpacity onPress={onCancel} style={dp.arrow} accessibilityLabel="닫기">
              <CancelIcon size={16} color={dp.arrowTxt.color} />
            </TouchableOpacity>
          </View>
          <Text style={{ color: TEXT_MUTED, fontSize: 11, marginBottom: 12 }}>
            예약 회차 시작 시각을 선택하세요.{bookedTimes.length > 0 ? ' 이미 예약된 시간은 흐리게 표시됩니다.' : ' 지나간 시간은 흐리게 표시됩니다.'}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingVertical: 4 }}>
            {TIME_SLOTS.map(t => {
              const past    = isPastSlot(t);
              const booked  = isBooked(t);
              const disabled = past || booked;
              const isSel   = t === selected;
              return (
                <TouchableOpacity
                  key={t}
                  style={[
                    tpRange.slot,
                    isSel    ? { backgroundColor: GOLD,     borderColor: GOLD    }
                    : booked ? { backgroundColor: SURFACE2, borderColor: RED,    opacity: 0.4 }
                    : past   ? { backgroundColor: SURFACE2, borderColor: BORDER, opacity: 0.35 }
                    :          { backgroundColor: SURFACE2, borderColor: BORDER },
                  ]}
                  onPress={() => { if (!disabled) setSelected(t === selected ? '' : t); }}
                  disabled={disabled}
                  activeOpacity={0.7}>
                  <Text style={[
                    tpRange.slotTxt,
                    { color: isSel ? BG : booked ? RED : past ? TEXT_MUTED : TEXT },
                  ]}>{t}</Text>
                  {isSel   && <Text style={[tpRange.slotLabel, { color: BG }]}>선택</Text>}
                  {booked  && !isSel && <Text style={[tpRange.slotLabel, { color: RED, opacity: 0.7 }]}>예약됨</Text>}
                </TouchableOpacity>
              );
            })}
          </View>
          {bookedTimes.length > 0 && (
            <View style={{ flexDirection:'row', alignItems:'center', gap:6, marginTop:12 }}>
              <View style={{ width:10, height:10, borderRadius:2, borderWidth:1, borderColor:RED, backgroundColor:COLORS.redBg }} />
              <Text style={{ color: TEXT_MUTED, fontSize: 11 }}>이미 예약된 시간</Text>
            </View>
          )}
          <View style={[dp.btnRow, { marginTop: 20 }]}>
            <SecondaryButton label="취소" onPress={onCancel} style={dp.cancelBtn} textStyle={dp.cancelTxt} />
            <GradientButton
              label={selected ? `${selected} 선택` : '시간을 선택해주세요'}
              onPress={() => selected ? onConfirm(selected) : null}
              disabled={!selected}
              style={dp.confirmBtn}
              textStyle={dp.confirmTxt}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

// 80차 계속: 등록되지 않은(직접 입력한) 테마는 실제 예약 슬롯이 없어 "이미 예약된
// 시간" 개념 자체가 무의미한데도 TimePickerModal의 10~22시 정각 버튼 그리드를 그대로
// 강제하고 있었음("19시반에 모이자" 같은 실제 모임 약속을 못 정함) — 모집탭 작성
// 편의성 개선 요청으로 발견. 등록된 테마(예약 슬롯 조회 가능)는 기존 버튼형 유지, 직접
// 입력 테마만 이 스크롤(드럼) 피커로 자유롭게 시/분을 고르게 분리.
const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '10', '20', '30', '40', '50'];

function FreeTimePickerModal({ visible, value, onConfirm, onCancel }) {
  const [h, m] = (value || '').split(':');
  const [selH, setSelH] = useState(h || '19');
  const [selM, setSelM] = useState(m || '00');

  useEffect(() => {
    if (visible) {
      const [vh, vm] = (value || '').split(':');
      setSelH(vh || '19');
      setSelM(vm || '00');
    }
  }, [visible]);

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <View style={dp2.overlay}>
        <View style={dp2.box}>
          <View style={dp2.modalHeader}>
            <Text style={dp2.modalTitle}>시간 선택</Text>
            <TouchableOpacity onPress={onCancel} accessibilityLabel="닫기">
              <CancelIcon size={dp2.modalClose.fontSize || 18} color={dp2.modalClose.color} />
            </TouchableOpacity>
          </View>
          <Text style={{ color: TEXT_MUTED, fontSize: 11, marginBottom: 4 }}>
            등록되지 않은 테마는 정해진 예약 시간이 없어, 원하는 시간을 자유롭게 스크롤해서 고를 수 있어요.
          </Text>
          <View style={dp2.pickerRow}>
            <View style={dp2.pickerCol}>
              <Text style={dp2.pickerLabel}>시</Text>
              <DrumPicker items={HOURS} selected={selH} onSelect={setSelH} />
            </View>
            <View style={dp2.pickerCol}>
              <Text style={dp2.pickerLabel}>분</Text>
              <DrumPicker items={MINUTES} selected={selM} onSelect={setSelM} />
            </View>
          </View>
          <View style={dp2.btnRow}>
            <SecondaryButton label="취소" onPress={onCancel} style={dp2.cancelBtn} textStyle={dp2.cancelTxt} />
            <GradientButton
              label={`${selH}:${selM} 선택`}
              onPress={() => onConfirm(`${selH}:${selM}`)}
              style={dp2.confirmBtn}
              textStyle={dp2.confirmTxt}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const tpRange = StyleSheet.create({
  slot:      { width: '22%', paddingVertical: 10, borderRadius: 8,
               alignItems: 'center', borderWidth: 1, minHeight: 44, justifyContent: 'center' },
  slotTxt:   { fontSize: 13, fontWeight: '600' },
  slotLabel: { fontSize: 9, color: BG, fontWeight: '800', marginTop: 1 },
});

// ── 달력 Modal ────────────────────────────────────────────────
function DatePickerModal({ visible, value, onConfirm, onCancel }) {
  const [year,  setYear]  = useState(value.getFullYear());
  const [month, setMonth] = useState(value.getMonth());
  const [day,   setDay]   = useState(value.getDate());
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDOW    = new Date(year, month, 1).getDay();
  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y); setDay(1);
  };
  const MONTH_NAMES = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];
  const DAY_NAMES   = ['일','월','화','수','목','금','토'];
  const isPast = (d) => { const t = new Date(); t.setHours(0,0,0,0); return new Date(year, month, d) < t; };
  const cells = [];
  for (let i = 0; i < firstDOW; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <View style={dp.overlay}>
        <View style={dp.box}>
          <View style={dp.header}>
            <TouchableOpacity onPress={() => changeMonth(-1)} style={dp.arrow} accessibilityLabel="이전 달"><Text style={dp.arrowTxt}>‹</Text></TouchableOpacity>
            <Text style={dp.title}>{year}년 {MONTH_NAMES[month]}</Text>
            <TouchableOpacity onPress={() => changeMonth(1)} style={dp.arrow} accessibilityLabel="다음 달"><Text style={dp.arrowTxt}>›</Text></TouchableOpacity>
          </View>
          <View style={dp.weekRow}>
            {DAY_NAMES.map((d, i) => (
              <Text key={d} style={[dp.weekDay, i===0&&{color:RED}, i===6&&{color:BLUE}]}>{d}</Text>
            ))}
          </View>
          <View style={dp.grid}>
            {cells.map((d, idx) => {
              const past = d !== null && isPast(d); const sel = d === day;
              return (
                <TouchableOpacity key={idx} style={[dp.cell, sel && dp.cellSel, past && dp.cellPast]}
                  onPress={() => !past && d !== null && setDay(d)} disabled={past || d === null}>
                  <Text style={[dp.cellTxt, sel && dp.cellTxtSel, past && dp.cellTxtPast, d === null && { opacity: 0 },
                    idx % 7 === 0 && !sel && { color: RED }, idx % 7 === 6 && !sel && { color: BLUE }]}>{d ?? ''}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={dp.btnRow}>
            <SecondaryButton label="취소" onPress={onCancel} style={dp.cancelBtn} textStyle={dp.cancelTxt} />
            <GradientButton
              label={`${year}년 ${MONTH_NAMES[month]} ${day}일 선택`}
              onPress={() => onConfirm(new Date(year, month, day))}
              style={dp.confirmBtn}
              textStyle={dp.confirmTxt}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const dp = StyleSheet.create({
  overlay:    { flex: 1, backgroundColor: 'rgba(0,0,0,0.8)', justifyContent: 'flex-end' },
  box:        { backgroundColor: SURFACE, borderTopLeftRadius: 24, borderTopRightRadius: 24,
                padding: 20, paddingBottom: 40, borderTopWidth: 1, borderTopColor: BORDER },
  header:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  arrow:      { padding: 8 },
  arrowTxt:   { color: GOLD, fontSize: 28, fontWeight: 'bold' },
  title:      { color: TEXT, fontSize: 17, fontWeight: '800' },
  weekRow:    { flexDirection: 'row', marginBottom: 8, borderBottomWidth: 1, borderBottomColor: BORDER, paddingBottom: 8 },
  weekDay:    { flex: 1, textAlign: 'center', color: TEXT_MUTED, fontSize: 12, fontWeight: '700', paddingVertical: 4 },
  grid:       { flexDirection: 'row', flexWrap: 'wrap' },
  cell:       { width: '14.28%', height: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
  cellSel:    { backgroundColor: GOLD },
  cellPast:   { opacity: 0.25 },
  cellTxt:    { color: TEXT, fontSize: 14, lineHeight: 20, textAlign: 'center' },
  cellTxtSel: { color: BG, fontWeight: '900' },
  cellTxtPast:{ color: TEXT_MUTED },
  btnRow:     { flexDirection: 'row', gap: 10, marginTop: 20 },
  cancelBtn:  { flex: 1 },
  cancelTxt:  {},
  confirmBtn: { flex: 2 },
  confirmTxt: { fontSize: 14 },
});

const dp2 = StyleSheet.create({
  overlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'flex-end' },
  box:         { backgroundColor: SURFACE, borderTopLeftRadius: 24, borderTopRightRadius: 24,
                 padding: 20, paddingBottom: 40, borderTopWidth: 1, borderTopColor: BORDER },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  modalTitle:  { color: GOLD, fontSize: 17, fontWeight: '800' },
  modalClose:  { color: TEXT_MUTED, fontSize: 20, padding: 4 },
  pickerRow:   { flexDirection: 'row', gap: 8, marginBottom: 20 },
  pickerCol:   { flex: 1 },
  pickerLabel: { color: TEXT_MUTED, fontSize: 11, fontWeight: '700', textAlign: 'center', marginBottom: 6 },
  drum:        { height: ITEM_H * 3, borderRadius: 12, overflow: 'hidden',
                 backgroundColor: SURFACE2, borderWidth: 1, borderColor: BORDER, position: 'relative' },
  highlight:   { position: 'absolute', top: ITEM_H, left: 0, right: 0, height: ITEM_H,
                 backgroundColor: GOLD + '18', borderTopWidth: 1, borderBottomWidth: 1,
                 borderColor: GOLD + '40', zIndex: 1 },
  drumItem:    { alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 },
  drumItemTxt: { color: TEXT_MUTED, fontSize: 13, fontWeight: '500' },
  drumItemTxtOn:{ color: GOLD, fontSize: 15, fontWeight: '800' },
  btnRow:      { flexDirection: 'row', gap: 10 },
  cancelBtn:   { flex: 1 },
  cancelTxt:   {},
  confirmBtn:  { flex: 2 },
  confirmTxt:  { fontSize: 14 },
});

// ── 테마 선택 Modal ───────────────────────────────────────────
function ThemePickerModal({ visible, onSelect, onClose, isMinor = false }) {
  const [themes,  setThemes]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  // 78차: 지역/장르 칩 필터 추가 — 전국 매장 테마를 텍스트 검색으로만 찾아야 해서
  // 불편하다는 신고로, 목록에 실제로 존재하는 지역/장르만 칩으로 뽑아 빠르게 좁힐 수 있게 함
  const [filterCity,  setFilterCity]  = useState(null);
  const [filterGenre, setFilterGenre] = useState(null);

  useEffect(() => {
    if (!visible) return;
    setLoading(true); setKeyword(''); setFilterCity(null); setFilterGenre(null);
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'stores'));
        const list = [];
        snap.docs.forEach(storeDoc => {
          const store = storeDoc.data();
          (store.branches || []).forEach((branch, bIdx) => {
            (branch.themes || []).forEach((theme, tIdx) => {
              list.push({
                productId:  `${storeDoc.id}_${bIdx}_${tIdx}`,
                name:       theme.name     || '',
                storeName:  getStoreDisplayName(store),
                branchName: branch.branchName || '',
                genre:      theme.genre    || '',
                city:       branch.city    || '',
                district:   branch.district || '',
                imageUrl:   theme.imageUrl  || '',
                maxPeople:  theme.maxPeople  || null,
                minPeople:  theme.minPeople  || 2,
                // 80차 후속: [버그 수정] 이 목록은 이미 동기로 로드돼 있는데 handleThemeSelect가
                // 형식/난이도를 별도 비동기 재조회(getProductById)로만 채우고 있어서, 그
                // 조회가 끝나기 전에 "모집 글 등록"을 누르면 이전 값(기본 room)이 그대로
                // 저장될 수 있었음(fork로 발견) — 애초에 여기 있는 값을 그대로 씀.
                bookingFormat: theme.bookingFormat || 'room',
                difficulty:    theme.difficulty || null,
                isAdultOnly:   !!theme.isAdultOnly,
              });
            });
          });
        });
        // 80차 후속: [버그 수정] 이 피커는 getAllProducts를 안 거치고 stores를 직접 조회해서
        // (isVisibleToViewer 필터 대상이 아님) 확인된 미성년자에게도 19금 테마가 그대로
        // 노출되고 있었음(실기기 테스트로 발견) — 모집글 작성 화면이라 19금 모집 자체를
        // 막는 것과 짝이 맞아야 함.
        setThemes(isMinor ? list.filter(t => !t.isAdultOnly) : list);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [visible, isMinor]);

  const cityOptions  = [...new Set(themes.map(t => t.city).filter(Boolean))];
  const genreOptions = [...new Set(themes.map(t => t.genre).filter(Boolean))];

  const filtered = themes.filter(t =>
    (!filterCity  || t.city  === filterCity) &&
    (!filterGenre || t.genre === filterGenre) &&
    (!keyword.trim() ||
      t.name.includes(keyword) || t.storeName.includes(keyword) ||
      t.branchName.includes(keyword) || t.city.includes(keyword))
  );

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={tp.overlay}>
        <View style={tp.box}>
          <View style={tp.header}>
            <IconLabel Icon={LogoMark} size={tp.title.fontSize || 16} color={tp.title.color} textStyle={tp.title}>테마 선택</IconLabel>
            <TouchableOpacity onPress={onClose} accessibilityLabel="닫기"><CancelIcon size={tp.close.fontSize || 18} color={tp.close.color} /></TouchableOpacity>
          </View>
          <TextInput style={tp.search} value={keyword} onChangeText={setKeyword}
            placeholder="테마명, 매장명, 지역으로 검색" placeholderTextColor={TEXT_MUTED}
            autoCorrect={false} />
          {(cityOptions.length > 0 || genreOptions.length > 0) && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 10 }}
              contentContainerStyle={{ gap: 6, paddingRight: 8 }}>
              {cityOptions.map(city => (
                <TouchableOpacity key={city}
                  style={[tp.chip, filterCity === city && tp.chipActive]}
                  onPress={() => setFilterCity(f => f === city ? null : city)}>
                  <IconLabel Icon={LocationIcon} size={13} color={filterCity === city ? GOLD : TEXT_MUTED} textStyle={[tp.chipText, filterCity === city && tp.chipTextActive]}>{city}</IconLabel>
                </TouchableOpacity>
              ))}
              {genreOptions.map(genre => (
                <TouchableOpacity key={genre}
                  style={[tp.chip, filterGenre === genre && tp.chipActive]}
                  onPress={() => setFilterGenre(f => f === genre ? null : genre)}>
                  <IconLabel Icon={GenreIcon} size={13} color={filterGenre === genre ? GOLD : TEXT_MUTED} textStyle={[tp.chipText, filterGenre === genre && tp.chipTextActive]}>{genre}</IconLabel>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
          {loading ? (
            <ActivityIndicator color={GOLD} style={{ marginTop: 32 }} />
          ) : filtered.length === 0 ? (
            <View style={{ alignItems: 'center', paddingTop: 40 }}>
              <Text style={{ color: TEXT_MUTED, fontSize: 14 }}>검색 결과가 없습니다</Text>
            </View>
          ) : (
            <FlatList
              data={filtered}
              keyExtractor={item => item.productId}
              renderItem={({ item }) => (
                <TouchableOpacity style={tp.item} onPress={() => onSelect(item)}>
                  <Text style={tp.itemName}>{item.name}</Text>
                  <Text style={tp.itemSub}>{item.storeName}{item.branchName ? ` · ${item.branchName}` : ''}</Text>
                  {(item.city || item.genre) && (
                    <View style={{ flexDirection: 'row', gap: 12, marginTop: 3 }}>
                      {item.city ? <IconLabel Icon={LocationIcon} size={13} color={TEXT_MUTED} textStyle={tp.itemSub}>{item.city}</IconLabel> : null}
                      {item.genre ? <IconLabel Icon={GenreIcon} size={13} color={TEXT_MUTED} textStyle={tp.itemSub}>{item.genre}</IconLabel> : null}
                    </View>
                  )}
                </TouchableOpacity>
              )}
              ItemSeparatorComponent={() => <View style={{ height: 1, backgroundColor: BORDER }} />}
            />
          )}
        </View>
      </View>
    </Modal>
  );
}

const tp = StyleSheet.create({
  overlay:  { flex: 1, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'flex-end' },
  box:      { backgroundColor: SURFACE, borderTopLeftRadius: 24, borderTopRightRadius: 24,
              padding: 20, paddingBottom: 40, borderTopWidth: 1, borderTopColor: BORDER, maxHeight: '80%' },
  header:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  title:    { color: GOLD, fontSize: 17, fontWeight: '800' },
  close:    { color: TEXT_MUTED, fontSize: 20, padding: 4 },
  search:   { backgroundColor: SURFACE2, borderRadius: 10, borderWidth: 1, borderColor: BORDER,
              paddingHorizontal: 14, paddingVertical: 10, color: TEXT, fontSize: 14, marginBottom: 4 },
  chip:       { borderRadius: 999, borderWidth: 1, borderColor: BORDER, backgroundColor: SURFACE2,
                paddingHorizontal: 12, paddingVertical: 7 },
  chipActive: { borderColor: GOLD, backgroundColor: 'rgba(216,174,82,0.14)' },
  chipText:       { color: TEXT_MUTED, fontSize: 12, fontWeight: '600' },
  chipTextActive: { color: GOLD },
  item:     { paddingVertical: 14, paddingHorizontal: 4 },
  itemName: { color: TEXT, fontSize: 15, fontWeight: '700', marginBottom: 4 },
  itemSub:  { color: TEXT_MUTED, fontSize: 12, lineHeight: 18 },
});

// ── 필드 래퍼 ────────────────────────────────────────────────
function F({ label, children, style }) {
  return (
    <View style={[s.field, style]}>
      <Text style={s.label}>{label}</Text>
      {children}
    </View>
  );
}

const toLocalDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const formatDate = (d) =>
  `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} (${['일','월','화','수','목','금','토'][d.getDay()]})`;

// ── 날짜 문자열 → Date 객체 (로컬 기준) ─────────────────────
const parseDateStr = (str) => {
  if (!str) return new Date();
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// ── 메인 ────────────────────────────────────────────────────
export default function RecruitWriteScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const { user, profile } = useAuth();

  // route.params.recruit: 수정 모드 / route.params.prefill: 재모집 미리채우기
  const editRecruit = route.params?.recruit || null;
  const prefill     = route.params?.prefill || null;
  const isEdit      = !!editRecruit;
  const isReRecruit = !!prefill && !isEdit;

  // 초기값 결정
  const initData = editRecruit || prefill || {};
  const parseRegion = (region = '') => {
    const parts = region.trim().split(' ');
    const main  = REGIONS.find(r => parts[0] === r) ? parts[0] : '';
    const sub   = main && parts.length > 1 ? parts.slice(1).join(' ') : '';
    return { main, sub };
  };
  const { main: initRegion, sub: initSubRegion } = parseRegion(initData.region);
  const initGenre = initData.genres?.[0] || initData.genre || '';

  // 74차: 크루원 모집으로 등록 — 내 크루가 있을 때만 토글 노출
  const [linkToCrew,         setLinkToCrew]         = useState(false);
  // 80차 계속: 승인된 참가자 전용 비공개 채팅 — 수정 모드에서도 켜고 끌 수 있어야 하므로
  // isEdit과 무관하게 initData 값으로 초기화.
  const [privateChatEnabled, setPrivateChatEnabled] = useState(initData.privateChatEnabled || false);
  const [title,              setTitle]              = useState(initData.title        || '');
  const [productId,          setProductId]          = useState(initData.productId    || '');
  const [productName,        setProductName]        = useState(initData.productName  || '');
  // 80차 후속: [버그 수정] 수정/재모집 모드는 handleThemeSelect를 안 거쳐 initData의
  // 값으로만 초기화되는데, 기본값 'room'으로 시작해 뒤늦게 도는 비동기 조회(useEffect
  // [productId])가 끝나기 전에 "수정 완료"를 누르면 체험형 모집글이 방탈출로 잘못
  // 되돌아갈 수 있었음 — recruits 문서에 이미 저장된 값이 있으면 그걸로 즉시 초기화.
  const [bookingFormat,      setBookingFormat]      = useState(initData.bookingFormat || 'room');
  const [themeDifficulty,    setThemeDifficulty]    = useState(initData.difficulty ?? null); // 80차 후속: [버그 수정] 실제 테마 난이도 반영(모집 목록 난이도 필터용)
  // 80차 후속: 19금 테마 모집 — 미성년 작성자 차단 + 참가자 나이 경고에 사용
  const [themeIsAdultOnly,   setThemeIsAdultOnly]   = useState(!!initData.isAdultOnly);
  const [autoFilled,         setAutoFilled]         = useState(false);
  const [selectedGenre,      setSelectedGenre]      = useState(initGenre);
  const [selectedRegion,     setSelectedRegion]     = useState(initRegion);
  const [selectedSubRegion,  setSelectedSubRegion]  = useState(initSubRegion);
  const [scheduledDate,      setScheduledDate]      = useState(
    isEdit ? parseDateStr(initData.scheduledDate)
    : isReRecruit ? (() => { const t = new Date(); t.setDate(t.getDate()+1); return t; })()
    : new Date()
  );
  const [scheduledTime,      setScheduledTime]      = useState(initData.scheduledTime || '');
  const [showDatePicker,     setShowDatePicker]     = useState(false);
  const [showTimePicker,     setShowTimePicker]     = useState(false);
  const [showThemePicker,    setShowThemePicker]    = useState(false);
  const [showLocGenrePicker, setShowLocGenrePicker] = useState(false);
  const [totalSlots,         setTotalSlots]         = useState(
    initData.totalSlots ? String(initData.totalSlots) : '4'
  );
  const [maxSlots,           setMaxSlots]           = useState(10);
  const [selectedImageUrl,   setSelectedImageUrl]   = useState(initData.imageUrl || '');
  const [openChatUrl,        setOpenChatUrl]        = useState(initData.kakaoLink || '');
  const [description,        setDescription]        = useState(initData.description || '');
  const [loading,            setLoading]            = useState(false);
  const [banChecked,         setBanChecked]         = useState(false);

  // 노쇼/신고 제재 진입 차단
  useEffect(() => {
    if (!user?.uid || isEdit) { setBanChecked(true); return; }
    checkRecruitBan(user.uid).then(result => {
      if (result.banned) {
        // 75차: reason으로 노쇼 제재 / 신고 처리 제재 문구 구분
        const msg = result.reason === 'report'
          ? `신고 처리 결과에 따라 모집글 작성이 제한됩니다.\n제한 해제일: ${new Date(result.banUntil).toLocaleDateString('ko-KR')}`
          : result.isPermanent
          ? '노쇼 3회 이상으로 모집 기능이 영구 정지되었습니다.'
          : `노쇼 제재 중입니다.\n제재 해제일: ${new Date(result.banUntil).toLocaleDateString('ko-KR')}`;
        showAlert('모집 불가', msg, [{ text: '확인', onPress: () => navigation.goBack() }]);
      } else {
        setBanChecked(true);
      }
    }).catch(() => setBanChecked(true));
  }, [user?.uid]);

  // 79차: [버그 수정] maxSlots가 오직 handleThemeSelect(테마 피커에서 새로 고를 때)
  // 에서만 실제 값(theme.maxPeople)으로 갱신되고 있었음 — 모집글 "수정"/"재모집"은
  // initData.productId로 기존 테마를 미리 채우기만 할 뿐 handleThemeSelect를 거치지
  // 않아, maxSlots가 하드코딩 기본값(10)에 그대로 머물러 있었음(전수 정독으로 발견).
  // 예: 정원 4명 테마로 만든 모집글을 수정하면 "2~10명"으로 뜨고 실제 방 정원을
  // 초과하는 인원으로 저장될 수 있었음 — productId가 세팅될 때마다(최초 로드 포함)
  // 실제 테마를 조회해 maxSlots를 다시 맞춤.
  useEffect(() => {
    // 80차 후속: [버그 수정] 체험형 테마를 골랐다가 직접입력(자유 텍스트)으로 바꾸면
    // productId가 비워지는데, 이 조기 반환이 bookingFormat을 리셋 안 해서 실제로는
    // 형식 정보가 없는 자유입력 모집글이 이전에 골랐던 테마의 형식(예: 체험형)으로
    // 잘못 저장/뱃지 표시될 수 있었음.
    if (!productId) { setThemeDifficulty(null); setBookingFormat('room'); setThemeIsAdultOnly(false); return; }
    getProductById(productId).then(theme => {
      if (theme?.maxPeople && theme.maxPeople > 1) setMaxSlots(theme.maxPeople);
      // 80차 후속: 체험형은 "정원을 채워야 방을 빌릴 수 있다"는 코디네이션 문제가 아니라
      // "같이 보고 싶어서" 모으는 것에 가까워 모집 설명 예시 문구만 다르게(구조는 동일 유지)
      setBookingFormat(theme?.bookingFormat || 'room');
      // 80차 후속: [버그 수정] 모집글 저장 시 difficulty가 항상 null로 고정돼 있어서
      // RecruitScreen.js의 난이도 필터가 "전체" 외엔 항상 결과 0건이었음(전수 정독으로
      // 발견) — 실제 테마 난이도로 채움. 체험형은 난이도 개념 자체가 없어(activityLevel을
      // 씀) 계속 null로 남겨 "체험형" 모집글이 특정 난이도로 잘못 분류되지 않게 함.
      setThemeDifficulty(theme?.bookingFormat === 'show' ? null : (theme?.difficulty || null));
      setThemeIsAdultOnly(!!theme?.isAdultOnly);
      // 80차 후속: [버그 수정, 실사용자 신고로 발견] 이 useEffect가 handleThemeSelect(테마
      // 피커에서 직접 고를 때)와 형제 관계인데 selectedImageUrl만 안 채우고 있었음 —
      // productId가 이 useEffect(피커를 거치지 않는 초기로드/수정/재모집/타 화면에서
      // productId를 미리 들고 진입하는 경로)로만 세팅되는 경우 항상 이미지가 빈 채로
      // 저장되고 있었음(프로덕션 recruits 표본 조사 결과 최근 5건 전부 imageUrl 빈값,
      // 연결된 테마는 전부 정상 이미지 보유 확인 — 이 화면엔 별도 사진 업로드 기능이
      // 없어 selectedImageUrl은 항상 테마 이미지를 그대로 따라가는 게 맞음).
      setSelectedImageUrl(theme?.imageUrl || '');
    }).catch(() => {});
  }, [productId]);

  // 예약된 시간 조회 (테마 + 날짜 기준)
  // 78차: reservations 직접 쿼리 대신 reservationService.getBookedTimes 재사용 —
  // firestore.rules 전수조사로 발견한 reservations list 구멍 수정의 일부(개인정보 없는
  // reservationSlots 기반으로 이관, 이 화면만 별도 로직을 안 들고 있도록 통일).
  const [bookedTimes, setBookedTimes] = useState([]);
  useEffect(() => {
    if (!productId || !scheduledDate) { setBookedTimes([]); return; }
    const dateStr = toLocalDateStr(scheduledDate);
    getBookedTimes(productId, dateStr)
      .then(set => setBookedTimes([...set]))
      .catch(() => setBookedTimes([]));
  }, [productId, scheduledDate]);

  // ── 테마 선택 콜백 ────────────────────────────────────────
  const handleThemeSelect = (theme) => {
    setProductId(theme.productId);
    setProductName(theme.name);
    setSelectedImageUrl(theme.imageUrl || '');
    // 80차 후속: [버그 수정] 이 값들은 픽커 목록에 이미 동기로 로드돼 있으므로 여기서
    // 바로 세팅 — productId 변경을 감지해 뒤늦게 도는 비동기 useEffect(getProductById)에만
    // 의존하면 그 조회가 끝나기 전에 제출할 경우 이전 값이 그대로 남는 레이스가 있었음.
    setBookingFormat(theme.bookingFormat || 'room');
    setThemeDifficulty(theme.bookingFormat === 'show' ? null : (theme.difficulty || null));
    setThemeIsAdultOnly(!!theme.isAdultOnly);
    if (theme.maxPeople && theme.maxPeople > 1) {
      setMaxSlots(theme.maxPeople);
      const cur = parseInt(totalSlots, 10) || 4;
      if (cur > theme.maxPeople) setTotalSlots(String(theme.maxPeople));
    } else {
      setMaxSlots(10);
    }
    let filled = false;
    if (theme.genre && GENRES.includes(theme.genre)) { setSelectedGenre(theme.genre); filled = true; }
    if (theme.city && REGIONS.includes(theme.city)) {
      setSelectedRegion(theme.city);
      // 78차 66차: theme.district가 SUB_REGIONS 고정 목록(동네 이름)에 없으면(예: 구 이름
      // "광진" — 목록엔 "건대"만 있음) 통째로 버리고 있었음 — 실제 매장 데이터인데 상세
      // 위치가 조용히 사라지는 원인이었음. 목록에 없어도 실제 값 그대로 채움.
      setSelectedSubRegion(theme.district || '');
      filled = true;
    }
    setAutoFilled(filled);
    setShowThemePicker(false);
  };

  const handleProductNameChange = (v) => {
    setProductName(v); setProductId(''); setAutoFilled(false);
  };

  const handleLocGenreConfirm = (genre, region, subRegion) => {
    setSelectedGenre(genre || '');
    setSelectedRegion(region);
    setSelectedSubRegion(subRegion || '');
    setAutoFilled(false);
    setShowLocGenrePicker(false);
  };

  const handleSubmit = async () => {
    if (!title.trim())       { showAlert('알림', '제목을 입력해주세요.'); return; }
    if (!productName.trim()) { showAlert('알림', '테마를 선택하거나 입력해주세요.'); return; }
    if (!selectedRegion)     { showAlert('알림', '지역을 선택해주세요.'); return; }
    const slots = parseInt(totalSlots, 10);
    if (!slots || slots < 2) { showAlert('알림', '모집 인원은 2명 이상이어야 합니다.'); return; }
    if (slots > maxSlots)    { showAlert('알림', `선택한 테마의 최대 인원은 ${maxSlots}명입니다.`); return; }
    // 79차: [버그 수정] updateRecruit()이 totalSlots를 무조건 덮어써서, 이미 승인된
    // 참가자가 있는 모집글을 수정할 때 그 인원수보다 적은 정원으로 낮출 수 있었음
    // (전수 정독으로 발견) — currentSlots(이미 승인된 인원) > totalSlots(새 정원)인
    // 모순 상태가 되면 bulkReserveForRecruit이 축소된 totalSlots를 실제 예약 인원으로
    // 그대로 기록해 통계/정산이 실제보다 적은 인원으로 잘못 잡힐 수 있었음.
    if (isEdit && editRecruit?.currentSlots > slots) {
      showAlert('알림', `이미 승인된 참가자가 ${editRecruit.currentSlots}명 있어 정원을 그보다 적게 줄일 수 없어요.`);
      return;
    }
    // 80차 후속: [기능 추가] 19금 테마는 미성년(또는 생년월일 미등록) 작성자가 애초에
    // 모집글을 올리지 못하게 차단 — 예약 화면의 클라이언트 소프트 게이트와 동일한 판정
    // 기준(isAdultBirth), 서버측 최종 방어는 실제 예약 시점(onNewReservation/
    // bulkReserveShowForRecruitCF)에 이미 있어 여기선 UX 차단만 담당.
    if (themeIsAdultOnly && !isAdultBirth(profile?.birth)) {
      showAlert('19세 이상 이용 가능', '이 테마는 만 19세 이상만 모집할 수 있어요.\n마이페이지에서 생년월일을 등록해주세요.');
      return;
    }
    Keyboard.dismiss();
    const payload = {
      productId:        productId || '',
      productName:      productName.trim(),
      storeName:        '',
      imageUrl:         selectedImageUrl || '',
      region:           selectedSubRegion ? `${selectedRegion} ${selectedSubRegion}` : selectedRegion || '',
      scheduledDate:    toLocalDateStr(scheduledDate),
      scheduledTime:    scheduledTime || '',
      scheduledTimeEnd: '',
      totalSlots:       slots,
      description:      description.trim() || '',
      kakaoLink:        openChatUrl.trim() || null,
      difficulty:       themeDifficulty,
      genres:           selectedGenre ? [selectedGenre] : [],
      bookingFormat, // 80차 후속: 모집 목록 필터/뱃지(방탈출·체험형 구분)용
      isAdultOnly: themeIsAdultOnly, // 80차 후속: 모집 목록/상세 "19금" 뱃지용 스냅샷
      privateChatEnabled, // 80차 계속: 승인된 참가자 전용 비공개 채팅 사용 여부
    };
    // 80차 후속: [기능 추가] 19금 테마 모집은 작성자가 참가자 전원의 나이를 보장할 수
    // 없으므로, 등록 직전에 "미성년 참가자가 있으면 현장 입장 거부/예약 취소될 수 있다"는
    // 경고를 한 번 더 확인받음(실제 서버 동작 — room은 참가자별 개별 취소, show는
    // 그룹 전체 거부 — 과 문구를 일치시킴).
    if (themeIsAdultOnly) {
      showAlert(
        '19금 테마 모집 안내',
        '이 테마는 만 19세 이상만 이용할 수 있어요.\n참가자 중 미성년자가 있으면 방문 시 입장이 거부되거나 예약이 취소될 수 있으니, 참가 신청을 받을 때 나이를 꼭 확인해주세요.',
        [
          { text: '취소', style: 'cancel' },
          { text: '등록', onPress: () => submitRecruit(payload) },
        ],
      );
      return;
    }
    await submitRecruit(payload);
  };

  const submitRecruit = async (payload) => {
    try {
      setLoading(true);
      if (isEdit) {
        await updateRecruit(editRecruit.id, { title: title.trim(), ...payload });
        showAlert('완료', '모집글이 수정되었습니다!', [{ text: '확인', onPress: () => navigation.goBack() }]);
      } else {
        const created = await createRecruit({
          uid:        user.uid,
          authorName: profile?.nickname || profile?.displayName || '익명',
          authorCrewName: profile?.crewName || null,
          title:      title.trim(),
          crewId:     linkToCrew && profile?.crewId ? profile.crewId : null,
          ...payload,
        });
        // 74차: 크루원 모집으로 등록했으면 크루 게시판에도 자동으로 카드가 나타나게 연동
        if (linkToCrew && profile?.crewId) {
          await addCrewRecruitLink({
            crewId: profile.crewId, uid: user.uid,
            nickname: profile?.nickname || profile?.displayName || '익명',
            recruitId: created.id, recruitTitle: title.trim(),
            productName: payload.productName, imageUrl: payload.imageUrl, region: payload.region,
            scheduledDate: payload.scheduledDate, scheduledTime: payload.scheduledTime, totalSlots: payload.totalSlots,
            bookingFormat: payload.bookingFormat, isAdultOnly: payload.isAdultOnly,
          }).catch(() => {});
        }
        showAlert('완료', '모집 글이 등록되었습니다!', [{ text: '확인', onPress: () => navigation.goBack() }]);
      }
    } catch (err) {
      console.error(err);
      showAlert('오류', isEdit ? '모집글 수정 중 오류가 발생했습니다.' : '모집 글 작성 중 오류가 발생했습니다.');
    } finally { setLoading(false); }
  };

  const locGenreParts = () => {
    const parts = [];
    if (selectedGenre) parts.push({ Icon: GenreIcon, text: selectedGenre });
    if (selectedRegion) {
      parts.push({ Icon: LocationIcon, text: `${selectedRegion}${selectedSubRegion ? ` ${selectedSubRegion}` : ''}` });
    }
    return parts;
  };

  const timeSummary = scheduledTime ? scheduledTime : null;

  if (!banChecked) return (
    <View style={{ flex:1, backgroundColor:BG, justifyContent:'center', alignItems:'center' }}>
      <ActivityIndicator size="large" color={GOLD} />
    </View>
  );

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: BG }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={insets.top}>
      <View style={s.root}>
        <AppHeader showBack
          centerElement={<Text style={s.headerTitle}>
            {isEdit ? '모집글 수정' : isReRecruit ? '재모집 글 작성' : '모집 글 작성'}
          </Text>}
        />

        <ScrollView
          style={s.scroll}
          contentContainerStyle={s.content}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          keyboardDismissMode="on-drag">

          {/* ── 노쇼 제재 안내 배너 (작성 모드에만 표시) ── */}
          {!isEdit && (
            <View style={s.noshowBanner}>
              <IconLabel Icon={NoShowPenaltyIcon} size={13} color={s.noshowBannerTitle.color} textStyle={s.noshowBannerTitle}>모집 노쇼 제재 안내</IconLabel>
              <Text style={s.noshowBannerBody}>
                모집 완료 후 <Text style={{ fontWeight:'800', color:ORANGE }}>7일 이내</Text> 예약 확정(일괄 예약)을 진행하지 않으면 노쇼로 처리됩니다.{'\n'}
                • 1회: 경고 + <Text style={{ fontWeight:'700' }}>7일</Text> 모집 기능 정지{'\n'}
                • 2회: <Text style={{ fontWeight:'700' }}>30일</Text> 모집 기능 정지{'\n'}
                • 3회 이상: <Text style={{ fontWeight:'700' }}>영구</Text> 모집 기능 정지
              </Text>
            </View>
          )}

          {/* 재모집 안내 배너 */}
          {isReRecruit && (
            <View style={s.reRecruitBanner}>
              <IconLabel Icon={RefreshIcon} size={12} color={s.reRecruitBannerTxt.color} textStyle={s.reRecruitBannerTxt}>
                기존 모집글 내용을 불러왔습니다. 날짜와 시간을 확인 후 등록해주세요.
              </IconLabel>
            </View>
          )}

          {/* 제목 */}
          <F label={`제목 * (${title.length}/40)`}>
            <TextInput style={s.input} value={title} onChangeText={v => setTitle(v.slice(0,40))}
              placeholder="모집 제목을 입력하세요" placeholderTextColor={TEXT_MUTED}
              returnKeyType="next" maxLength={40} />
          </F>

          {/* 78차 35차: "크루원 모집으로 등록"이 폼 맨 아래(오픈채팅 링크 뒤)에 있어
              눈에 잘 안 띈다는 피드백 — 제목 바로 아래로 이동해 작성 초반부터 보이게 함. */}
          {!isEdit && !!profile?.crewId && (
            <TouchableOpacity style={s.crewToggleRow} onPress={() => setLinkToCrew(v => !v)} activeOpacity={0.8}>
              <View style={[s.crewToggleCheck, linkToCrew && s.crewToggleCheckOn]}>
                {linkToCrew && <CheckmarkIcon size={12} color={BG} />}
              </View>
              <Text style={s.crewToggleText}>[{profile.crewName}] 크루원 모집으로 등록</Text>
            </TouchableOpacity>
          )}

          {/* 80차 계속: 승인된 참가자 전용 비공개 채팅 — 공개 댓글과 별개로, 전화번호·상세
              집합 장소 등을 참가자끼리만 나눌 수 있는 채널. 수정 모드에서도 켜고 끌 수 있음. */}
          <TouchableOpacity style={s.crewToggleRow} onPress={() => setPrivateChatEnabled(v => !v)} activeOpacity={0.8}>
            <View style={[s.crewToggleCheck, privateChatEnabled && s.crewToggleCheckOn]}>
              {privateChatEnabled && <CheckmarkIcon size={12} color={BG} />}
            </View>
            <Text style={s.crewToggleText}>참가자 전용 비공개 채팅 켜기</Text>
          </TouchableOpacity>

          {/* 테마 선택 */}
          <F label="테마 *">
            <TouchableOpacity style={s.themeSelectBtn} onPress={() => setShowThemePicker(true)}>
              {productName && productId ? (
                <View>
                  <Text style={s.themeSelectedName}>{productName}</Text>
                  <Text style={s.themeSelectedSub}>탭하여 변경</Text>
                </View>
              ) : (
                <IconLabel Icon={LogoMark} size={16} color={GOLD} textStyle={s.themeSelectPlaceholder}>등록된 테마에서 선택하기 →</IconLabel>
              )}
            </TouchableOpacity>
            <TextInput style={[s.input, { marginTop: 8 }]}
              value={productName} onChangeText={handleProductNameChange}
              placeholder="또는 직접 테마명 입력" placeholderTextColor={TEXT_MUTED} />
          </F>

          {/* 자동 채움 안내 */}
          {autoFilled && (
            <View style={s.autoFillBanner}>
              <Text style={s.autoFillBannerText}>
                ✨ 테마 정보를 바탕으로 장르와 지역을 자동으로 채워드렸어요.
              </Text>
            </View>
          )}

          {/* 장르 · 지역 */}
          <F label="장르 · 지역 *">
            <TouchableOpacity
              style={[s.selectorBtn, selectedRegion && s.selectorBtnFilled]}
              onPress={() => setShowLocGenrePicker(true)}>
              {locGenreParts().length > 0 ? (
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', gap: 14 }}>
                    {locGenreParts().map((p, i) => (
                      <IconLabel key={i} Icon={p.Icon} size={13} color={GOLD} textStyle={s.selectorBtnTxt}>{p.text}</IconLabel>
                    ))}
                  </View>
                  <Text style={s.selectorBtnSub}>탭하여 변경</Text>
                </View>
              ) : (
                <Text style={s.selectorBtnPlaceholder}>장르 / 지역 / 상세지역 선택 →</Text>
              )}
              <Text style={{ color: GOLD, fontSize: 18 }}>›</Text>
            </TouchableOpacity>
          </F>

          {/* 날짜 · 시간 */}
          <View style={s.dateTimeRow}>
            <F label="예정 날짜 *" style={{ flex: 1 }}>
              <TouchableOpacity style={s.dateBtn} onPress={() => setShowDatePicker(true)}>
                <IconLabel Icon={MenuReservationIcon} size={12} color={TEXT} textStyle={s.dateTxt} numberOfLines={1}>{formatDate(scheduledDate)}</IconLabel>
                <Text style={s.dateArrow}>›</Text>
              </TouchableOpacity>
            </F>
            <F label="예정 시간" style={{ flex: 1 }}>
              <TouchableOpacity style={s.dateBtn} onPress={() => setShowTimePicker(true)}>
                <IconLabel Icon={MenuPointsIcon} size={12} color={timeSummary ? TEXT : TEXT_MUTED}
                  textStyle={[s.dateTxt, !timeSummary && { color: TEXT_MUTED }]} numberOfLines={1}>
                  {timeSummary || '시간 선택'}
                </IconLabel>
                <Text style={s.dateArrow}>›</Text>
              </TouchableOpacity>
            </F>
          </View>

          {/* 모집 인원 */}
          <F label={`모집 인원 (본인 포함, 2~${maxSlots}명) *`}>
            <View style={s.chipRow}>
              {Array.from({ length: Math.min(maxSlots, 8) - 1 }, (_, i) => i + 2).map(n => (
                <TouchableOpacity key={n} style={[s.chip, totalSlots === String(n) && s.chipOn]}
                  onPress={() => setTotalSlots(String(n))}>
                  <Text style={[s.chipTxt, totalSlots === String(n) && s.chipTxtOn]}>{n}명</Text>
                </TouchableOpacity>
              ))}
              {maxSlots > 8 && (
                <TextInput style={[s.input, { width: 72, paddingVertical: 8, textAlign: 'center' }]}
                  value={totalSlots} onChangeText={(v) => {
                    const n = parseInt(v, 10);
                    if (!v) { setTotalSlots(''); return; }
                    if (n > maxSlots) setTotalSlots(String(maxSlots));
                    else setTotalSlots(v);
                  }}
                  keyboardType="numeric" placeholder="직접" placeholderTextColor={TEXT_MUTED} />
              )}
            </View>
            {maxSlots < 10 && (
              <Text style={{ color: GREEN, fontSize: 11, marginTop: 6, fontWeight: '600' }}>
                ✨ 선택한 테마의 최대 인원은 {maxSlots}명입니다
              </Text>
            )}
          </F>

          {/* 모집 설명 */}
          <F label={`모집 설명 (${description.length}/300)`}>
            <TextInput style={[s.input, s.textArea]} value={description}
              onChangeText={v => setDescription(v.slice(0, 300))}
              placeholder={bookingFormat === 'show' ? SHOW_DESC_PLACEHOLDER : DESC_PLACEHOLDER}
              placeholderTextColor={TEXT_MUTED}
              multiline numberOfLines={6} textAlignVertical="top" maxLength={300} />
          </F>

          {/* 오픈채팅 링크 (설명 아래로 이동) */}
          <F label="오픈채팅 링크">
            <TextInput style={s.input} value={openChatUrl} onChangeText={setOpenChatUrl}
              placeholder="https://open.kakao.com/..." placeholderTextColor={TEXT_MUTED}
              autoCapitalize="none" keyboardType="url" returnKeyType="done" />
          </F>

          {/* 하단 등록/저장 버튼 */}
          <GradientButton
            label={loading ? '처리 중...' : (isEdit ? '수정 완료' : '모집 글 등록')}
            onPress={handleSubmit}
            disabled={loading}
            style={s.submitBtn}
            textStyle={s.submitTxt}
          />

          <View style={{ height: 48 }} />
        </ScrollView>

        {/* 모달들 */}
        <LocationGenreModal
          visible={showLocGenrePicker}
          genre={selectedGenre}
          region={selectedRegion}
          subRegion={selectedSubRegion}
          onConfirm={handleLocGenreConfirm}
          onCancel={() => setShowLocGenrePicker(false)}
        />
        <DatePickerModal
          visible={showDatePicker}
          value={scheduledDate}
          onConfirm={(d) => { setScheduledDate(d); setShowDatePicker(false); }}
          onCancel={() => setShowDatePicker(false)}
        />
        {productId ? (
          <TimePickerModal
            visible={showTimePicker}
            value={scheduledTime}
            selectedDate={scheduledDate}
            bookedTimes={bookedTimes}
            onConfirm={(t) => { setScheduledTime(t); setShowTimePicker(false); }}
            onCancel={() => setShowTimePicker(false)}
          />
        ) : (
          <FreeTimePickerModal
            visible={showTimePicker}
            value={scheduledTime}
            onConfirm={(t) => { setScheduledTime(t); setShowTimePicker(false); }}
            onCancel={() => setShowTimePicker(false)}
          />
        )}
        <ThemePickerModal
          visible={showThemePicker}
          onSelect={handleThemeSelect}
          onClose={() => setShowThemePicker(false)}
          isMinor={isConfirmedMinorBirth(profile?.birth)}
        />
      </View>
    </KeyboardAvoidingView>
  );
}

const s = StyleSheet.create({
  root:        { flex: 1, backgroundColor: BG },
  headerTitle: { color: COLORS.text, fontSize: 15, fontWeight: '800' },
  scroll:      { flex: 1 },
  content:     { padding: 20 },
  field:       { marginBottom: 20 },
  label:       { fontSize: 12, color: TEXT_SEC, marginBottom: 8, fontWeight: '700', letterSpacing: 0.3 },
  input:       { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER,
                 borderRadius: 10, padding: 14, color: TEXT, fontSize: 15 },
  textArea:    { minHeight: 120, paddingTop: 12 },
  chipRow:     { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip:        { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1,
                 borderColor: BORDER, backgroundColor: SURFACE },
  chipOn:      { backgroundColor: GOLD_BG, borderColor: GOLD },
  chipTxt:     { color: TEXT_MUTED, fontSize: 13 },
  chipTxtOn:   { color: GOLD, fontWeight: '700' },

  selectorBtn: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 10,
                 padding: 14, flexDirection: 'row', alignItems: 'center', gap: 8 },
  selectorBtnFilled: { borderColor: GOLD_BDR, backgroundColor: GOLD_BG },
  selectorBtnTxt:    { color: GOLD, fontSize: 14, fontWeight: '700', flex: 1 },
  selectorBtnSub:    { color: TEXT_MUTED, fontSize: 11, marginTop: 2 },
  selectorBtnPlaceholder: { color: TEXT_MUTED, fontSize: 14, flex: 1 },

  dateTimeRow: { flexDirection: 'row', gap: 10, marginBottom: 0 },
  dateBtn:     { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 10,
                 padding: 12, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  dateTxt:     { color: TEXT, fontSize: 13, flex: 1 },
  dateArrow:   { color: GOLD, fontSize: 18 },

  themeSelectBtn:         { backgroundColor: GOLD_BG, borderWidth: 1, borderColor: GOLD, borderRadius: 10, padding: 14 },
  themeSelectedName:      { color: GOLD, fontSize: 15, fontWeight: '700' },
  themeSelectedSub:       { color: TEXT_MUTED, fontSize: 11, marginTop: 2 },
  themeSelectPlaceholder: { color: GOLD, fontSize: 14, fontWeight: '600' },
  autoFillBanner:         { backgroundColor: COLORS.greenBg, borderRadius: 10, padding: 12,
                            borderWidth: 1, borderColor: COLORS.greenBorder, marginBottom: 16, marginTop: -8 },
  autoFillBannerText:     { color: GREEN, fontSize: 12, lineHeight: 18, fontWeight: '600' },

  noshowBanner: {
    backgroundColor: COLORS.orangeBg, borderRadius: 12,
    borderWidth: 1, borderColor: ORANGE + '59', padding: 14, marginBottom: 20,
  },
  noshowBannerTitle: { color: ORANGE, fontSize: 13, fontWeight: '800', marginBottom: 6 },
  noshowBannerBody:  { color: 'rgba(255,255,255,0.55)', fontSize: 12, lineHeight: 20 },

  reRecruitBanner: {
    backgroundColor: GOLD_BG, borderRadius: 10,
    borderWidth: 1, borderColor: GOLD_BDR, padding: 12, marginBottom: 16,
  },
  reRecruitBannerTxt: { color: GOLD, fontSize: 12, lineHeight: 18, fontWeight: '600' },

  submitBtn:   { marginTop: 8 },
  submitTxt:   { fontSize: 16 },
  // 74차: 크루원 모집 등록 토글
  crewToggleRow:      { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  crewToggleCheck:     { width: 20, height: 20, borderRadius: 5, borderWidth: 1.5, borderColor: GOLD_BDR, justifyContent: 'center', alignItems: 'center' },
  crewToggleCheckOn:   { backgroundColor: GOLD, borderColor: GOLD },
  crewToggleText:      { color: GOLD, fontSize: 13, fontWeight: '700' },
});